/* global sessionStorage */

import * as React from 'react';
import classnames from 'classnames';
import disclaimerText from '../../data/disclaimer';
import Button from '../Button/Button';
import { gaGrantConsent, gaTrackCategoryEvent } from '../../data/ga';
import { isDisclaimerAccepted } from '../../utils/helpers/disclaimer';
import { StateContext } from '../../context/context';
import auth from '../../helpers/auth';
import { MODAL_DISCLAIMER_ENABLED } from '../../config/featureFlags';
import { container, disclaimer, disclaimerActions, disclaimerParagraph, header, proceed, visible, wrapper } from './Disclaimer.module.scss';

const isServerEnv = typeof window === 'undefined';
const gaTrackDisclaimer = gaTrackCategoryEvent('disclaimer');

let userTrackingStarted = false;

export const DisclaimerText = disclaimerText.split('\n').map((p, index) =>
    p ? (
        <p className={disclaimerParagraph} key={index}>
            {p}
        </p>
    ) : null
);

let startedReadingDisclaimerTime = 0;

const Disclaimer = ({ setDisclaimer, deferAutoShow = false }) => {
    const disclaimerScrollerRef = React.useRef(null);
    const disclaimerEnabled = MODAL_DISCLAIMER_ENABLED;
    const {
        disclaimer: { showDisclaimer }
    } = React.useContext(StateContext);

    const handleTouchMove = event => {
        // Disables safari scroll bounce
        if (!disclaimerScrollerRef.current) return;
        const rect = disclaimerScrollerRef.current.getBoundingClientRect();
        const startY = event.touches[0].pageY;
        if (startY < rect.top || startY > rect.bottom - rect.y) {
            event.preventDefault();
        }
    };

    const authenticated = auth().isAuthenticated();

    React.useEffect(() => {
        if (isServerEnv) {
            return;
        }

        if (!disclaimerEnabled) {
            setDisclaimer(false);
            return;
        }

        if (!authenticated) {
            setDisclaimer(false);
            return;
        }

        if (isDisclaimerAccepted()) {
            setDisclaimer(false);

            if (!userTrackingStarted) {
                gaGrantConsent();
                userTrackingStarted = true;
            }
        } else if (!deferAutoShow) {
            setDisclaimer(true);
        }
    }, [authenticated, deferAutoShow, disclaimerEnabled, setDisclaimer]);

    React.useEffect(() => {
        if (!disclaimerEnabled) {
            return;
        }

        window.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [disclaimerEnabled]);

    React.useEffect(() => {
        if (!disclaimerEnabled) {
            return;
        }

        if (showDisclaimer && !isServerEnv) {
            startedReadingDisclaimerTime = new Date().getTime();
        }
    }, [disclaimerEnabled, showDisclaimer]);

    const acceptDisclaimer = () => {
        const currentTimestamp = new Date().getTime();
        sessionStorage.setItem('readDisclaimer', currentTimestamp);

        gaGrantConsent();
        gaTrackDisclaimer('disclaimer_accepted', (currentTimestamp - startedReadingDisclaimerTime) / 1000);
        userTrackingStarted = true;

        setDisclaimer(false);
    };

    return showDisclaimer && disclaimerEnabled ? (
        <div className={classnames(container, visible)}>
            <div className={disclaimer}>
                <div ref={disclaimerScrollerRef} className={wrapper}>
                    <h1 className={header}>Disclaimer</h1>
                    {DisclaimerText}
                </div>
                <div className={disclaimerActions}>
                    <Button onClick={acceptDisclaimer}>I accept</Button>
                    <span className={proceed}>Please read the disclaimer to proceed.</span>
                </div>
            </div>
        </div>
    ) : null;
};

export default Disclaimer;
